import React from 'react'
import { useModalBehavior } from 'hooks/useModalBehavior'
import { formatCurrency } from 'utils/currencyFormatter'
import { interpolate } from 'utils/i18nUtils'

// Confirm step for a package the student's discount covers in full. Such an
// order skips the invoice and payment, so it used to be created on the first
// click. The welcome coupon is one-time: whatever this order does not use is
// voided (`preview.welcome_voided`, computed by the server). This dialog shows
// the receipt and that amount before the order exists.
//
// `preview` is the discount_preview response: original_amount, welcome_applied,
// referral_applied, welcome_voided, final_amount.
export default function FullDiscountConfirmModal({ isOpen, onClose, onConfirm, packageName, currency = 'TWD', preview, translations, submitting = false }) {
  const { handleOverlayClick } = useModalBehavior(isOpen, onClose)

  if (!isOpen || !preview) return null

  const t = translations?.fullDiscountConfirm || {}
  const money = (amount) => formatCurrency(amount, currency)
  const welcomeApplied = Number(preview.welcome_applied) || 0
  const referralApplied = Number(preview.referral_applied) || 0
  const voided = Number(preview.welcome_voided) || 0

  const row = (label, value, valueClass = 'text-gray-600') => (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-gray-800 min-w-0">{label}</span>
      <span className={`shrink-0 ${valueClass}`}>{value}</span>
    </div>
  )

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4"
      onClick={handleOverlayClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="full-discount-confirm-title"
        className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col"
      >
        <div
          className="flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ backgroundColor: '#E6F0FF' }}
        >
          <span id="full-discount-confirm-title" className="font-semibold" style={{ color: '#3366FF' }}>{t.title || 'Confirm your discount'}</span>
          <button type="button" onClick={onClose} className="p-3 -m-3 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer" aria-label={t.close || 'Close'}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-3 text-sm">
          {row(packageName, money(preview.original_amount))}
          {welcomeApplied > 0 && row(t.welcomeLine || 'Welcome coupon', `−${money(welcomeApplied)}`, 'text-emerald-700')}
          {referralApplied > 0 && row(t.referralLine || 'Referral discount', `−${money(referralApplied)}`, 'text-emerald-700')}
          <div className="border-t border-gray-200 pt-3 font-semibold">
            {row(t.totalLine || 'You pay', money(Math.max(0, Number(preview.final_amount) || 0)), 'text-gray-900')}
          </div>

          {voided > 0 && (
            <div className="flex gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-red-800 leading-relaxed">
              <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <span>{interpolate(t.voidedLine || 'The %{amount} left on your welcome coupon will be voided. The coupon can be used once.', { amount: money(voided) })}</span>
            </div>
          )}
        </div>

        <div className="shrink-0 px-5 py-4 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
          >
            {t.cancel || 'See other packages'}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="min-h-11 px-6 py-2 text-white text-sm font-medium rounded-lg transition-opacity cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#3366FF' }}
          >
            {t.confirm || 'Use it'}
          </button>
        </div>
      </div>
    </div>
  )
}
